/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useTranslations } from '@/hooks/useTranslations';
import IntegrationMonitorPage from '@/pages/Monitor/IntegrationMonitorPage';
import PersonalizationMonitorPage from '@/pages/Monitor/PersonalizationMonitorPage';
import { useGetProductByFlowIdQuery } from '@/store/apis';
import { Button, IllustratedMessage } from '@ui5/webcomponents-react';
import { Link, Navigate, useParams } from 'react-router-dom';

export function getMonitorStrategyBySkuId(skuId: number): 'Integration' | 'Personalization' {
    if (skuId === 1 || skuId === 2) return 'Integration';
    else return 'Personalization';
}

export default function MonitorLayout() {
    const { t } = useTranslations();
    const { flowId } = useParams();

    const {
        data: productDto,
        isLoading,
        error,
    } = useGetProductByFlowIdQuery(flowId!, {
        skip: !flowId,
    });

    if (!flowId) return <Navigate to="/" />;

    if (isLoading) {
        // Loading state is handled globally
        return null;
    }

    if (error || !productDto) {
        console.error('Something went wrong', error);
        return (
            <IllustratedMessage
                name="ErrorScreen"
                titleText={t('MONITOR_LAYOUT_ERROR_TITLE_TEXT')}
                subtitleText={t('MONITOR_LAYOUT_ERROR_SUBTITLE_TEXT')}
            >
                <Link data-sap-ui-fastnavgroup="true" to="/">
                    <Button>{t('MONITOR_LAYOUT_BACK_TO_HOME_BUTTON_TEXT')}</Button>
                </Link>
            </IllustratedMessage>
        );
    }

    const monitorStrategy = getMonitorStrategyBySkuId(productDto.sku);

    if (monitorStrategy === 'Integration') return <IntegrationMonitorPage flowId={flowId} skuId={productDto.sku} />;
    return <PersonalizationMonitorPage flowId={flowId} skuId={productDto.sku} />;
}
